import {
  Badge,
  DropdownOption,
  DropdownSelectedValue,
  Widget,
} from '../../../ui';

import { IconAngleRight, IconComment } from '@maia/icons';
import { BrokerDto, TaskDto } from '@maia/shared';
import { getDifferenceInDaysLabelFromNow } from '@maia/utils';
import { ActionButtonLink } from '../../../atoms/actionButtonLink/actionButtonLink';
import { DropdownOptionAvatar } from '../../../forms/dropdownOptions/dropdownOptionAvatar/dropdownOptionAvatar';
import { DropdownOptionStatus } from '../../../forms/dropdownOptions/dropdownOptionStatus/dropdownOptionStatus';
import { DropdownSelectedAvatarValue } from '../../../forms/dropdownSelectedValues/dropdownSelectedAvatarValue/dropdownSelectedAvatarValue';
import { DropdownSelectedStatusValue } from '../../../forms/dropdownSelectedValues/dropdownSelectedStatusValue/dropdownSelectedStatusValue';
import { BadgeDropdown } from '../../../molecules/badgeDropdown/badgeDropdown';

type Props = {
  task: TaskDto;
  brokers: Array<BrokerDto>;
  onBrokerChange: (broker: string) => void;
  onPriorityChange: (priority: string) => void;
  onStatusChange: (status: string) => void;
};

export function EditableTaskWidget(props: Props) {
  const { task, brokers, onBrokerChange, onPriorityChange, onStatusChange } =
    props;

  return (
    <Widget
      title={task.title}
      subtitle={task.description}
      actions={[
        <ActionButtonLink
          icon={IconAngleRight}
          size="small"
          to={`/tasks/$guid`}
          params={{ guid: task.guid }}
        />,
      ]}
      {...(task.estimatedEndDate
        ? {
            timestamp: {
              showIcon: true,
              date: getDifferenceInDaysLabelFromNow(task?.estimatedEndDate),
            },
          }
        : {})}
      padding="large"
    >
      <Badge
        icon={IconComment}
        label={task.noteCount.toString()}
        spacing="small"
      />
      <BadgeDropdown
        selectedKey={task.assignee?.guid ?? ''}
        className="inline-flex"
        items={[
          {
            value: '',
            label: 'Niet toegewezen',
            meta: {},
          },
          ...brokers.map((broker) => ({
            label: `${broker.firstName} ${broker.lastName}`,
            value: broker.guid,
            meta: {
              initials: `${broker.firstName[0] ?? broker.initials[0]}`,
            },
          })),
        ]}
        placeholder="Kies een broker"
        onChange={(broker) => onBrokerChange(broker.toString())}
        renderOption={(item, itemInfo) => {
          if (!item.value) {
            return <DropdownOption label={item.label} {...itemInfo} />;
          }
          return (
            <DropdownOptionAvatar
              label={item.label}
              variant="square"
              avatar={{
                initials: item.meta.initials,
              }}
            />
          );
        }}
        renderSelectedValue={(item) => {
          if (!item.value) {
            return <DropdownSelectedValue label={item.label} size="small" />;
          }
          return (
            <DropdownSelectedAvatarValue
              label={item.label}
              variant="square"
              avatar={{ initials: item.meta.initials }}
            />
          );
        }}
        enforceMaxHeight
      />
      <BadgeDropdown
        className="inline-flex"
        selectedKey={task.priority.value}
        items={[
          {
            value: 'low',
            label: 'Laag',
            meta: {
              color: 'success',
              variant: 'square',
            },
          },
          {
            value: 'medium',
            label: 'Gemiddeld',
            meta: {
              color: 'info',
              variant: 'square',
            },
          },
          {
            value: 'high',
            label: 'Hoog',
            meta: {
              color: 'warning',
              variant: 'square',
            },
          },
          {
            value: 'critical',
            label: 'Kritiek',
            meta: {
              color: 'error',
              variant: 'square',
            },
          },
        ]}
        placeholder="Kies een prioriteit"
        onChange={(priority) => onPriorityChange(priority.toString())}
        renderSelectedValue={(item) => {
          if (!item.value) {
            return <DropdownSelectedValue label={item.label} size="small" />;
          }
          return (
            <DropdownSelectedStatusValue
              status={item.meta}
              label={item.label}
            />
          );
        }}
        renderOption={(item, itemInfo) => {
          if (!item.value) {
            return <DropdownOption label={item.label} {...itemInfo} />;
          }
          return <DropdownOptionStatus label={item.label} status={item.meta} />;
        }}
        enforceMaxHeight
      />

      <BadgeDropdown
        className="inline-flex"
        selectedKey={task.status.value}
        items={[
          {
            value: 'in_review',
            label: 'Open',
            meta: {},
          },
          {
            value: 'in_progress',
            label: 'In uitvoering',
            meta: {},
          },
          {
            value: 'closed',
            label: 'Afgerond',
            meta: {},
          },
          {
            value: 'postponed',
            label: 'In afwachting',
            meta: {},
          },
        ]}
        placeholder="Kies een status"
        onChange={(status) => onStatusChange(status.toString())}
        enforceMaxHeight
      />
    </Widget>
  );
}
